import React, { lazy, Suspense, useEffect, useRef } from 'react'
import { useContainerWidth, LayoutBand, colors, Divider, usePageWidth, useInfoSheet, Grid } from '@fs/zion-ui'
import { useParams, useHistory, useQueryParams } from '@fs/zion-router'
import type { Person } from '@fs/zion-tree-types'
import { GedcomWarningAlert, useGroupsContext, WikiTreeBanner } from '@fs/zion-tree-groups'
import { ScoreInfoSheet } from '@fs/zion-person-quality-score'
import { GedcomInfoSheet } from '@fs/zion-controlled-edit-trees'
import { useShowTempleContext } from '../providers/ShowTempleContext'
import ConclusionDetailedViewProvider from '../providers/ConclusionDetailedViewProvider'
import { useExpandedSections } from '../providers/ExpandedSectionsProvider'
import useFocusPerson from '../data-service/useFocusPerson'
import usePubSub from '../data-service/usePubSub'
import type { AllPerson } from '../data-service/useObsoletePerson'
import useObsoletePerson from '../data-service/useObsoletePerson'
import LargeLoadingSpinner from '../common/LargeLoadingSpinner'
import Banners, { ConfidentialBanner, LivingBanner, ReadOnlyBanner } from './components/banners/Banners'
import PersonPageTabs from './components/PersonPageTabs'
import PersonBanner from './components/person-banner/PersonBanner'
import MobilePersonBanner from './components/person-banner/MobilePersonBanner'
import MiniHeader from './components/mini-header/MiniHeader'
import MobileMiniHeader from './components/mini-header/MobileMiniHeader'
import usePersonQualityScore from '../data-service/usePersonQualityScore'
import useGedcomData from '../data-service/useGedcomData'
import ShowGedcomAlertProvider from '../providers/GivePriorityToGedcomAlertProvider'
import ResearchAssistantCard from './cards/research-assistant/ResearchAssistantCard'
import ContributionsProvider from '../providers/ContributionsProvider'
import WikiTreeProvider, { useWikiTree } from '../providers/WikiTreeProvider'

// Tab Contents
const DetailsTab = lazy(() => import('./tabs/DetailsTab'))
const AboutTab = lazy(() => import('./tabs/AboutTab'))
const MemoriesTab = lazy(() => import('./tabs/MemoriesTab'))
const OrdinancesTab = lazy(() => import('./tabs/OrdinancesTab'))
const UndoMergeDialog = lazy(() => import('./components/tools/UndoMergeDialog'))
const CollaborateTab = lazy(() => import('./tabs/CollaborateTab'))
const TimelineTab = lazy(() => import('./tabs/TimelineTab'))

// Individual Cards not grouped into a Tab content component
const VitalsCard = lazy(() => import('./cards/vitals-card/VitalsCard'))
const NonVitalsTab = lazy(() => import('./tabs/NonVitalsTab'))
const FamilyMembersCard = lazy(() => import('./cards/family-members-card/FamilyMembersCard'))
const SourcesCard = lazy(() => import('./cards/sources-card/SourcesCard'))
const ToolsCard = lazy(() => import('./cards/tools-card/ToolsCard'))
const SearchRecordsCard = lazy(() => import('./cards/search-records-card/SearchRecordsCard'))

export const vitalTypes = ['BIRTH', 'DEATH', 'CHRISTENING', 'BURIAL', 'GENDER', 'NAME']

const subPageContentMap = {
  about: AboutTab,
  details: DetailsTab,
  memories: MemoriesTab,
  ordinances: OrdinancesTab,
  vitals: VitalsCard,
  'non-vitals': NonVitalsTab,
  family: FamilyMembersCard,
  sources: SourcesCard,
  tools: ToolsCard,
  'search-records': SearchRecordsCard,
  collaborate: CollaborateTab,
  timeline: TimelineTab,
}

function redirectIfObsoletePerson(
  focusPerson: Person | undefined,
  possibleObsoletePerson: AllPerson | undefined
): void {
  if (focusPerson && possibleObsoletePerson?.obsoleteIdWasRequested) {
    window.location.replace(`/tree/person/details/${possibleObsoletePerson.id}?innerId=${focusPerson.id}`)
  }
}

function InternalPersonPage(): JSX.Element {
  const { query } = useQueryParams()
  // openDetail is for backwards compatibility with old v8 links
  const { conclusion = query.openDetail, completedMergeDuplicateId, coupleRelationshipId } = query
  const infoSheetSize = usePageWidth()({ default: 'sm', xxl: 'md' } as const)

  const scoreInfoSheet = useInfoSheet()
  const atContainerWidth = useContainerWidth()
  const isMobile = atContainerWidth({ lg: false, default: true })
  const [showTemple, showTempleLoadingState] = useShowTempleContext()

  const [expandedSections] = useExpandedSections()
  const { focusPerson, notFound: personNotFound, isLoading: loadingFocusPerson, error } = useFocusPerson()
  const notFound = personNotFound || error?.response?.status === 410

  const { score, showPersonQualityScore, mutatePersonQualityScore } = usePersonQualityScore()

  const { possibleObsoletePerson } = useObsoletePerson(focusPerson)
  redirectIfObsoletePerson(focusPerson, possibleObsoletePerson)

  const hasExpandedForAutoOpen = useRef(false)

  // If the conclusion query param is present, we need to force either vitals or other-information to be open.
  if (expandedSections && (conclusion || coupleRelationshipId) && hasExpandedForAutoOpen.current === false) {
    hasExpandedForAutoOpen.current = true
    if (vitalTypes.includes(conclusion)) {
      expandedSections.vitals = true
    } else if (conclusion) {
      expandedSections['other-information'] = true
    }
    if (coupleRelationshipId) {
      expandedSections.family = true
    }
  }

  const wikiTreeOverlay = useWikiTree()
  const history = useHistory()
  const { pid, subPage } = useParams()
  const { setPidInvite, currentGroup } = useGroupsContext()
  const { data: researchTreeGedcomData, isLoading: isLoadingResearchTreeGedcomData, mutate } = useGedcomData()
  const currentGroupId = currentGroup?.id
  usePubSub()
  const gedcomErrorsInfoSheet = useInfoSheet()

  // If a non-member user tries to navigate to /ordinances/{pid}, redirect them to /details/{pid}
  useEffect(() => {
    if (!showTemple && showTempleLoadingState === 'loaded' && subPage === 'ordinances') {
      history.replace(`/details/${pid}`)
    }
  }, [history, pid, showTempleLoadingState, showTemple, subPage])

  useEffect(() => {
    if (pid) setPidInvite?.(pid)
  }, [pid, setPidInvite])

  const Content: (typeof subPageContentMap)[keyof typeof subPageContentMap] = subPageContentMap[subPage]
  const hasGedcomAbnormalTermination = researchTreeGedcomData?.status === 'ABNORMAL_TERMINATION'
  const hasUnreviewedGedcomDifferences =
    researchTreeGedcomData && researchTreeGedcomData.errorCount > 0 && !researchTreeGedcomData.errorReviewDone
  const showGedcomAlert = hasGedcomAbnormalTermination || hasUnreviewedGedcomDifferences
  const givePriorityToGedcomAlert = showGedcomAlert || isLoadingResearchTreeGedcomData

  return (
    // This div has minHeight of 100vh so the page content will _always_ be at least 100vh tall.
    // This prevents the page from jumping nearly as often when we switch tabs. Since some tab contents are
    // short, it would cause the page to jump up, which is jarring.
    <div style={{ minHeight: '100vh' }}>
      {!loadingFocusPerson && (
        <>
          <WikiTreeBanner overlay={wikiTreeOverlay} />
          {(focusPerson || notFound) &&
            (!isMobile ? (
              <PersonBanner focusPerson={focusPerson} />
            ) : (
              <MobilePersonBanner focusPerson={focusPerson} scoreInfoSheet={scoreInfoSheet} />
            ))}
        </>
      )}
      {focusPerson &&
        (!isMobile ? (
          <MiniHeader focusPerson={focusPerson} showTemple={showTemple} />
        ) : (
          <MobileMiniHeader focusPerson={focusPerson} showTemple={showTemple} />
        ))}
      {focusPerson && <Banners person={focusPerson} />}
      {notFound && <Banners notFound />}
      {completedMergeDuplicateId && <UndoMergeDialog duplicateId={completedMergeDuplicateId} />}
      {focusPerson && (
        <>
          <Divider />

          <LayoutBand data-testid="main-tabs" maxWidth="lg" color={colors.gray00}>
            <PersonPageTabs showTemple={showTemple} />
          </LayoutBand>

          <LayoutBand maxWidth="lg" marginY="nano" marginX="nano">
            <Grid gutters="nano">
              <ResearchAssistantCard focusPerson={focusPerson} />
              {focusPerson.readOnly && <ReadOnlyBanner />}
              {focusPerson.living && !givePriorityToGedcomAlert && <LivingBanner treeType={focusPerson.treeType} />}
              {!focusPerson.visibleToAllWhenUsingFamilySearchApps &&
                !focusPerson.living &&
                !givePriorityToGedcomAlert && <ConfidentialBanner treeType={focusPerson.treeType} />}
              {showGedcomAlert && (
                <>
                  {/* eslint-disable-next-line test-selectors/onClick -- onClick already has a data-testid inside the component */}
                  <GedcomWarningAlert
                    onClick={gedcomErrorsInfoSheet.handleClick}
                    status={researchTreeGedcomData.status}
                  />
                </>
              )}
              <Suspense fallback={<LargeLoadingSpinner />}>
                <ShowGedcomAlertProvider givePriorityToGedcomAlert={givePriorityToGedcomAlert}>
                  <Content
                    focusPerson={focusPerson}
                    totalColumns={12}
                    currentGroupId={currentGroupId}
                    scoreInfoSheet={scoreInfoSheet}
                  />
                </ShowGedcomAlertProvider>
              </Suspense>
            </Grid>
          </LayoutBand>
        </>
      )}
      {showPersonQualityScore && score && (
        <ScoreInfoSheet
          overlay={scoreInfoSheet}
          score={score}
          size={infoSheetSize}
          onDismissIssue={() => mutatePersonQualityScore()}
        />
      )}

      <GedcomInfoSheet
        fileName={researchTreeGedcomData?.fileName}
        infoSheet={gedcomErrorsInfoSheet}
        size={infoSheetSize}
        onDoneReviewing={() => mutate()}
      />
    </div>
  )
}

export default function PersonPage(): JSX.Element {
  return (
    <ConclusionDetailedViewProvider>
      <ContributionsProvider pageSize={3}>
        <WikiTreeProvider>
          <InternalPersonPage />
        </WikiTreeProvider>
      </ContributionsProvider>
    </ConclusionDetailedViewProvider>
  )
}
